import { useEffect, useState } from 'react';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text,
} from '@tremor/react';
import DateRangePresets from './DateRangePresets';
import EmptyState from './EmptyState';
import ErrorRetry from './ErrorRetry';
import TableSkeleton from './TableSkeleton';
import { fetchPartRastreo } from '../api/part';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { PartRastreoBundle, PartRastreoEventType } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

function kindBadge(k: PartRastreoEventType) {
  const color =
    k === 'receipt' ? 'emerald' :
    k === 'customer_return' ? 'blue' :
    k === 'po_return' ? 'amber' :
    'red'; // sale
  return <Badge color={color}>{t.part.rastreo.kinds[k]}</Badge>;
}

interface Props {
  line: string;
  part: string;
}

export default function PartRastreoSection({ line, part }: Props) {
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [data, setData] = useState<PartRastreoBundle | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    setData(null);
    fetchPartRastreo(line, part, { from, to })
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [line, part, from, to]);

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && !data && <TableSkeleton rows={6} cols={5} />}

      {data && (
        <>
          <Card className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Kpi label={t.part.rastreo.kpi.receipts}    value={String(data.summary.receipts)} />
            <Kpi label={t.part.rastreo.kpi.poReturns}   value={String(data.summary.po_returns)} />
            <Kpi label={t.part.rastreo.kpi.sales}       value={String(data.summary.sales)} />
            <Kpi label={t.part.rastreo.kpi.custReturns} value={String(data.summary.customer_returns)} />
            <Kpi label={t.part.rastreo.kpi.net}         value={String(data.summary.net)} />
          </Card>

          <Card className="p-0 overflow-hidden">
            <Text className="px-4 pt-3 font-semibold">{t.part.rastreo.perStore.title}</Text>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.part.rastreo.perStore.sid}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.part.rastreo.perStore.startInstk}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.part.rastreo.perStore.net}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.part.rastreo.perStore.expected}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.part.rastreo.perStore.currentInstk}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.part.rastreo.perStore.discrepancy}</TableHeaderCell>
                  <TableHeaderCell>{t.part.rastreo.perStore.phydate}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.per_store.map((s) => {
                  const hasAnchor = typeof s.instk_at_start === 'number';
                  const expected = hasAnchor ? (s.instk_at_start as number) + s.net_qty : null;
                  const discrepancy = expected != null ? s.current_instk - expected : null;
                  return (
                    <TableRow key={s.sid}>
                      <TableCell className="font-medium">{s.sid}</TableCell>
                      <TableCell className="text-right">
                        {hasAnchor ? s.instk_at_start : (
                          <span className="text-tremor-content text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{s.net_qty}</TableCell>
                      <TableCell className="text-right">
                        {expected == null ? '—' : expected}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{s.current_instk}</TableCell>
                      <TableCell className="text-right">
                        {discrepancy == null
                          ? '—'
                          : (
                            <Badge color={discrepancy === 0 ? 'emerald' : discrepancy > 0 ? 'amber' : 'red'}>
                              {discrepancy > 0 ? `+${discrepancy}` : String(discrepancy)}
                            </Badge>
                          )}
                      </TableCell>
                      <TableCell>{s.phydate ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {data.per_store.some((s) => s.instk_at_start == null) && (
              <Text className="px-4 pb-3 text-xs text-tremor-content">
                {t.part.rastreo.perStore.noAnchor}
              </Text>
            )}
          </Card>

          {data.events.length === 0 ? (
            <EmptyState message={t.part.rastreo.empty} />
          ) : (
            <Card className="p-0 overflow-hidden">
              <Text className="px-4 pt-3 font-semibold">{t.part.rastreo.events.title}</Text>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.part.rastreo.events.date}</TableHeaderCell>
                    <TableHeaderCell>{t.part.rastreo.events.kind}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.part.rastreo.events.qty}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.part.rastreo.events.sid}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.part.rastreo.events.ref}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.part.rastreo.events.price}</TableHeaderCell>
                    <TableHeaderCell>{t.part.rastreo.events.counterparty}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.events.map((e, i) => (
                    <TableRow key={`${e.tdate}-${e.sid}-${e.ref_num}-${i}`}>
                      <TableCell>{e.tdate}</TableCell>
                      <TableCell>{kindBadge(e.event_type)}</TableCell>
                      <TableCell className="text-right">
                        <span className={e.qty < 0 ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}>
                          {e.qty > 0 ? `+${e.qty}` : String(e.qty)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{e.sid}</TableCell>
                      <TableCell className="text-right">{e.ref_num}</TableCell>
                      <TableCell className="text-right">{fmtMxn.format(e.unit_price_mxn)}</TableCell>
                      <TableCell>{e.counterparty || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold">{value}</div>
    </div>
  );
}
